import { Checkbox } from '@components/ui/checkbox';
import { FormDescription, FormErrorMessage, FormField, FormLabel } from '@components/ui/form/shared.component';
import { useFieldContext } from './form';

type CheckboxFieldProps = React.ComponentProps<typeof Checkbox> & {
  className?: string;
  description?: string;
  label: string;
};

/**
 * A wrapper component over the basic Checkbox that adds TanStack Form logic and validation.
 * Provides form field context, error handling, accessibility features, and optional description text.
 * @param props - All other standard HTML checkbox props plus custom styling and content options
 * @returns A form-integrated checkbox field component with label and optional description
 */
export function CheckboxField(props: CheckboxFieldProps) {
  const { className, description, label, ...otherProps } = props;
  const field = useFieldContext<boolean>();

  return (
    <FormField className={className}>
      <div className="flex items-center space-x-2">
        <Checkbox
          checked={field.state.value || false}
          id={field.name}
          name={field.name}
          onBlur={field.handleBlur}
          onCheckedChange={(checked) => field.handleChange(checked === true)}
          {...otherProps}
        />
        <FormLabel
          htmlFor={field.name}
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          {label}
        </FormLabel>
      </div>
      {description && <FormDescription className="pl-6">{description}</FormDescription>}
      <FormErrorMessage />
    </FormField>
  );
}
